import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Copy, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCloseMatchRequest, useMatchHistoryDetail, type MatchRequestTeacherRow } from '@/hooks/useTeacherMatchHistory';

interface Props {
  requestId: string | null;
  onOpenChange: (open: boolean) => void;
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch { return iso; }
}

function statusBadge(s: string) {
  if (s === 'confirmed') return <Badge className="bg-green-600 hover:bg-green-600">ตอบรับ</Badge>;
  if (s === 'denied') return <Badge variant="destructive">ปฏิเสธ</Badge>;
  if (s === 'pending') return <Badge variant="secondary">รอตอบ</Badge>;
  return <Badge variant="outline">{s}</Badge>;
}

function lineBadge(s: string | null) {
  if (s === 'sent') return <Badge variant="outline" className="border-green-600 text-green-700">ส่งสำเร็จ</Badge>;
  if (s === 'failed') return <Badge variant="outline" className="border-destructive text-destructive">ส่งล้มเหลว</Badge>;
  return <Badge variant="outline">-</Badge>;
}

type AcademicItem = { degree?: string; univ?: string; faculty?: string; major?: string; gpa?: string };

function parseAcademic(raw: string | null | undefined): { items: AcademicItem[]; rawText: string | null } {
  if (!raw) return { items: [], rawText: null };
  const trimmed = raw.trim();
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      const items = arr.filter((x) => x && typeof x === 'object') as AcademicItem[];
      if (items.length > 0) return { items, rawText: null };
    } catch { /* fall through */ }
  }
  return { items: [], rawText: raw };
}

function formatAcademicLine(it: AcademicItem): string {
  const parts: string[] = [];
  if (it.degree) parts.push(it.degree);
  if (it.univ) parts.push(it.univ);
  const facMaj = [it.faculty, it.major].filter(Boolean).join(' ');
  if (facMaj) parts.push(facMaj);
  let line = parts.join(' — ');
  if (it.gpa) line += ` (GPA ${it.gpa})`;
  return line || '-';
}

function shortAcademicForTemplate(raw: string | null | undefined): string {
  const { items } = parseAcademic(raw);
  if (items.length > 0) {
    const it = items[0];
    const facMaj = [it.faculty, it.major].filter(Boolean).join(' ');
    return [it.univ, facMaj].filter(Boolean).join(' ').trim();
  }
  return '';
}

function buildSaleTemplate(studentLabel: string, confirmed: MatchRequestTeacherRow[]): string {
  const studentName = studentLabel.split(' — ')[0] || studentLabel;
  const lines: string[] = [];
  lines.push(`ได้รายชื่อครูที่ตอบรับสำหรับน้อง ${studentName} แล้ว ดังนี้:`);
  lines.push('');
  confirmed.forEach((t, i) => {
    const name = `${t.teacher_name || t.teacher_code}${t.teacher_nickname ? ` (${t.teacher_nickname})` : ''}`;
    lines.push(`${i + 1}. ${name} — ${t.teacher_code}`);
    const edu = shortAcademicForTemplate(t.academic_achievements);
    if (edu) lines.push(`   ${edu}`);
  });
  lines.push('');
  lines.push('ขอให้ Sale ส่งให้ผู้ปกครองเลือกครูค่ะ');
  return lines.join('\n');
}

export const MatchHistoryDetailDialog: React.FC<Props> = ({ requestId, onOpenChange }) => {
  const { data, isLoading, error } = useMatchHistoryDetail(requestId || undefined);
  const closeMut = useCloseMatchRequest();
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [saleMessage, setSaleMessage] = useState<string>('');
  const [messageDirty, setMessageDirty] = useState(false);

  const confirmedTeachers = useMemo(
    () => (data?.teachers || []).filter((t) => t.status === 'confirmed'),
    [data]
  );

  const defaultTemplate = useMemo(
    () => (data ? buildSaleTemplate(data.request.student_label, confirmedTeachers) : ''),
    [data, confirmedTeachers]
  );

  useEffect(() => {
    if (!messageDirty) setSaleMessage(defaultTemplate);
  }, [defaultTemplate, messageDirty]);

  // Reset state when dialog reopens for a new request
  useEffect(() => {
    setMessageDirty(false);
    setSaleMessage('');
  }, [requestId]);

  const isExpired = data ? new Date(data.request.expire_at).getTime() < Date.now() : false;
  const canClose = !!data && data.request.status === 'pending' && !isExpired;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(saleMessage);
      toast.success('คัดลอกข้อความเรียบร้อย');
    } catch {
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  const handleResetTemplate = () => {
    setSaleMessage(defaultTemplate);
    setMessageDirty(false);
  };

  const handleClose = () => {
    if (!requestId) return;
    closeMut.mutate(requestId, {
      onSuccess: () => {
        toast.success('ปิดรับการตอบรับเรียบร้อย');
        setCloseConfirmOpen(false);
      },
      onError: (e: any) => toast.error(e?.message || 'ปิดไม่สำเร็จ'),
    });
  };

  return (
    <Dialog open={!!requestId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>รายละเอียดคำขอจับคู่ครู</DialogTitle>
          <DialogDescription>
            {data ? data.request.student_label : 'กำลังโหลด...'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> กำลังโหลด...
          </div>
        )}
        {error && (
          <div className="text-destructive text-sm">เกิดข้อผิดพลาด: {(error as Error).message}</div>
        )}

        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat label="ส่งทั้งหมด" value={data.request.counts.total} />
              <Stat label="ส่ง LINE สำเร็จ" value={data.request.counts.sent} />
              <Stat label="ตอบรับ" value={data.request.counts.confirmed} valueClass="text-green-700" />
              <Stat label="ปฏิเสธ" value={data.request.counts.denied} valueClass="text-destructive" />
            </div>

            <div className="rounded-md border p-3 text-sm space-y-1 bg-muted/30">
              <div><span className="text-muted-foreground">สร้างโดย:</span> {data.request.created_by_email || '-'}</div>
              <div><span className="text-muted-foreground">สร้างเมื่อ:</span> {fmt(data.request.created_at)}</div>
              <div><span className="text-muted-foreground">หมดอายุ:</span> {fmt(data.request.expire_at)}</div>
              <div><span className="text-muted-foreground">สถานะคำขอ:</span> {data.request.status}</div>
              {canClose && (
                <div className="pt-2">
                  <Button size="sm" variant="destructive" onClick={() => setCloseConfirmOpen(true)}>
                    <XCircle className="h-4 w-4 mr-1" /> ปิดรับการตอบรับ
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-semibold">ครูที่ตอบรับ ({data.teachers.filter(t => t.status === 'confirmed').length})</div>
              {data.teachers.filter(t => t.status === 'confirmed').length === 0 ? (
                <div className="text-sm text-muted-foreground rounded-md border p-3 bg-muted/20">ยังไม่มีครูตอบรับ</div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {confirmedTeachers.map(t => {
                    const acad = parseAcademic(t.academic_achievements);
                    return (
                    <div key={t.id} className="rounded-md border p-3 bg-background space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium text-sm">
                            {t.teacher_name || t.teacher_code}
                            {t.teacher_nickname ? ` (${t.teacher_nickname})` : ''}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">{t.teacher_code}</div>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">{fmt(t.responded_at)}</div>
                      </div>
                      {(acad.items.length > 0 || acad.rawText) && (
                        <div className="text-xs">
                          <span className="text-muted-foreground">การศึกษา: </span>
                          {acad.items.length > 0 ? (
                            <ul className="list-disc ml-5 space-y-0.5">
                              {acad.items.map((it, i) => (
                                <li key={i}>{formatAcademicLine(it)}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="whitespace-pre-wrap">{acad.rawText}</span>
                          )}
                        </div>
                      )}
                      {t.teaching_experience && (
                        <div className="text-xs">
                          <span className="text-muted-foreground">ประสบการณ์: </span>
                          <span className="whitespace-pre-wrap line-clamp-3">{t.teaching_experience}</span>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {confirmedTeachers.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">ข้อความสำหรับส่งให้ Sale</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleResetTemplate} disabled={!messageDirty}>
                      <RotateCcw className="h-4 w-4 mr-1" /> รีเซ็ต
                    </Button>
                    <Button size="sm" onClick={handleCopy}>
                      <Copy className="h-4 w-4 mr-1" /> คัดลอกข้อความ
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={saleMessage}
                  onChange={(e) => { setSaleMessage(e.target.value); setMessageDirty(true); }}
                  rows={Math.min(16, Math.max(6, saleMessage.split('\n').length + 1))}
                  className="font-mono text-xs"
                />
              </div>
            )}

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ครู</TableHead>
                    <TableHead>สถานะ LINE</TableHead>
                    <TableHead>การตอบ</TableHead>
                    <TableHead>เวลาที่ตอบ</TableHead>
                    <TableHead>เหตุผล / Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.teachers.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">ไม่มีครู</TableCell></TableRow>
                  )}
                  {data.teachers.map(t => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div className="font-medium">
                          {t.teacher_name || t.teacher_code}
                          {t.teacher_nickname ? ` (${t.teacher_nickname})` : ''}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">{t.teacher_code}</div>
                      </TableCell>
                      <TableCell>{lineBadge(t.line_send_status)}</TableCell>
                      <TableCell>{statusBadge(t.status)}</TableCell>
                      <TableCell className="text-sm">{fmt(t.responded_at)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[260px]">
                        {t.deny_reason || t.line_error || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {data.request.message_preview && (
              <div className="rounded-md border p-3 text-xs whitespace-pre-wrap bg-muted/20">
                <div className="text-muted-foreground mb-1 font-medium">ข้อความตัวอย่าง</div>
                {data.request.message_preview}
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <AlertDialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันปิดรับการตอบรับ?</AlertDialogTitle>
            <AlertDialogDescription>
              หลังจากปิดแล้ว ครูที่ยังไม่ได้ตอบรับจะกดยืนยันไม่ได้อีก การกระทำนี้ไม่สามารถยกเลิกได้
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closeMut.isPending}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={handleClose} disabled={closeMut.isPending}>
              {closeMut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              ยืนยันปิด
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

const Stat: React.FC<{ label: string; value: number; valueClass?: string }> = ({ label, value, valueClass }) => (
  <div className="rounded-md border p-3 bg-background">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-xl font-semibold ${valueClass || ''}`}>{value}</div>
  </div>
);
